import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [error, setError] = useState("");
  const [impersonator, setImpersonator] = useState(null); // {id,email} si impersonation en cours

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setImpersonator(data.impersonator || null);
    } catch {
      setUser(false);
      setImpersonator(null);
    }
  }, []);

  const impersonate = async (userId) => {
    const { data } = await api.post(`/admin/users/${userId}/impersonate`);
    setUser(data.user);
    setImpersonator(data.impersonator || null);
    return data.user;
  };

  const stopImpersonation = async () => {
    const { data } = await api.post("/admin/stop-impersonation");
    setUser(data.user);
    setImpersonator(null);
    return data.user;
  };

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const quickAdmin = async (loginCode) => {
    setError("");
    try {
      const { data } = await api.post("/auth/quick-admin", { login_code: loginCode });
      setUser(data.user);
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setImpersonator(null);
  };

  return (
    <AuthContext.Provider value={{ user, error, setError, login, quickAdmin, register, logout, refresh, impersonator, impersonate, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
